// Import required libraries
import React, { useState, useEffect } from 'react';
import { http, ConfigContext } from '../../../../../base-ui/lib';
import { Button, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableRow, Paper, Grid, TableHead, Typography } from '@mui/material';
import { ceil } from 'lodash';

// Initial data extracted from the spreadsheet
const postConfig = [
    { material: '2x10x8', qty: 2, col6: 8.75, col7: '', col8: 7.583333, col9: '', col10: 6.75, col11: '', col12: 6.166667, col13: '', col14: 5.75, col15: '', col16: 5.333333, col17: '', col18: 5 },
    { material: '2x10x10', qty: 2, col6: 10.333333, col7: '', col8: 9, col9: '', col10: 8, col11: '', col12: 7.333333, col13: '', col14: 6.75, col15: '', col16: 6.333333, col17: '', col18: 6 },
    { material: '2x10x12', qty: 2, col6: 12.166667, col7: '', col8: 10.583333, col9: '', col10: 9.416667, col11: '', col12: 8.583333, col13: '', col14: 8, col15: '', col16: 7.5, col17: '', col18: 7 },
    { material: '2x10x8', qty: 3, col6: 10.833333333, col7: '', col8: 9.5, col9: '', col10: 8.5, col11: '', col12: 7.75, col13: '', col14: 7.16666667, col15: '', col16: 6.6666667, col17: '', col18: 6.3333333 },
    { material: '2x10x10', qty: 3, col6: 13, col7: '', col8: 11.25, col9: '', col10: 10, col11: '', col12: 9.16666667, col13: '', col14: 8.5, col15: '', col16: 7.91666667, col17: '', col18: 7.5 },
    { material: '2x10x12', qty: 3, col6: 15.25, col7: '', col8: 13.25, col9: '', col10: 11.0833333333, col11: '', col12: 10.75, col13: '', col14: 10, col15: '', col16: 9.333333333, col17: '', col18: 8.83333333333 }
];

// React Component
const DynamicSpreadsheet = () => {
    const [data, setData] = useState(postConfig);
    const [widthOfDeck, setWidthOfDeck] = useState(0);
    const [postsData, setPostsData] = useState([]);
    const { config } = React.useContext(ConfigContext);


    // Add useEffect to fetch company configuration for postConfig
    useEffect(() => {
        const fetchCompanyConfig = async () => {
            try {
                // Fetch the company configuration from the API
                const response = await http.get({}, `${config.uri.base}/post/config`);

                // If postConfig exists in the response, update the state
                if (response && response.postConfig) {
                    setData(response.postConfig);
                }
            } catch (error) {
                console.error('Error fetching company configuration:', error);
            }
        };

        fetchCompanyConfig();
    }, [config.uri.base]);

    // Function to add a new row
    const addRow = () => {
        const newRow = { material: '2x', qty: 1, col6: '', col7: '', col8: '', col9: '', col10: '', col11: '', col12: '', col13: '', col14: '', col15: '', col16: '', col17: '', col18: '' };
        setData([...data, newRow]);
    };

    // Function to handle input change
    const handleInputChange = (rowIndex, key, value) => {
        const newData = data.map((row, rIndex) => (
            rIndex === rowIndex ? { ...row, [key]: value } : row
        ));
        setData(newData);
        const updatedData = recalculateValues(newData);
        sendDataToServer(updatedData[rowIndex], rowIndex);
    };

    // Function to send data to server
    const sendDataToServer = async (updatedRow, rowIndex) => {
        try {
            await http.put({ ...updatedRow, rowIndex }, `${config.uri.base}/post/config`);
        } catch (error) {
            console.error('Error sending data to server:', error);
        }
    };

    // Function to handle width of deck change
    const handleWidthChange = (value) => {
        setWidthOfDeck(value);
        calculatePostsNeeded(value);
    };

    // Function to recalculate odd-numbered columns
    const recalculateValues = (newData) => {
        const updatedData = newData.map((row) => {
            const updatedRow = { ...row };
            Object.keys(updatedRow).forEach((key) => {
                if (key.startsWith('col') && parseInt(key.replace('col', '')) % 2 !== 0 && parseInt(key.replace('col', '')) >= 7 && parseInt(key.replace('col', '')) <= 17) {
                    const leftKey = `col${parseInt(key.replace('col', '')) - 1}`;
                    const rightKey = `col${parseInt(key.replace('col', '')) + 1}`;
                    const leftValue = parseFloat(updatedRow[leftKey]) || 0;
                    const rightValue = parseFloat(updatedRow[rightKey]) || 0;
                    updatedRow[key] = parseFloat(((leftValue + rightValue) / 2).toFixed(6));
                }
            });
            return updatedRow;
        });
        setData(updatedData);
        return updatedData;
    };

    // Function to calculate number of posts needed
    const calculatePostsNeeded = (deckWidth) => {
        if (deckWidth <= 0) return;
        const updatedPostsData = data.map((row) => {
            const updatedRow = { ...row };
            Object.keys(updatedRow).forEach((key) => {
                if (key.startsWith('col') && parseInt(key.replace('col', '')) >= 6) {
                    const beamValue = parseFloat(updatedRow[key]);
                    if (beamValue && deckWidth) {
                        updatedRow[key] = ceil(deckWidth / beamValue) + 1; // Round up to nearest whole number and add 1
                    }
                }
            });
            return updatedRow;
        });
        setPostsData(updatedPostsData);
    };

    // Render the tables
    return (
        <div>
            <Typography variant="h6">Posts Configuration</Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: '60px', padding: '4px' }}>Material</TableCell>
                            <TableCell style={{ width: '60px', padding: '4px' }}>Qty</TableCell>
                            {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((col) => (
                                <TableCell key={col} style={{ width: '60px', padding: '4px' }}>{col}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>


                    <TableBody>
                        {data.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                <TableCell style={{ width: '60px', padding: '4px' }}>
                                    <TextField
                                        value={row.material}
                                        onChange={(e) => handleInputChange(rowIndex, 'material', e.target.value)}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        inputProps={{ style: { fontSize: '12px' } }}
                                    />
                                </TableCell>
                                <TableCell style={{ width: '60px', padding: '4px' }}>
                                    <TextField
                                        value={row.qty}
                                        type="number"
                                        onChange={(e) => handleInputChange(rowIndex, 'qty', e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        inputProps={{ style: { fontSize: '12px' } }}
                                    />
                                </TableCell>
                                {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((col) => (
                                    <TableCell key={col} style={{ width: '60px', padding: '4px' }}>
                                        <TextField
                                            value={parseFloat(row[`col${col}`])}
                                            onChange={(e) => handleInputChange(rowIndex, `col${col}`, parseFloat(e.target.value))}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            inputProps={{ style: { fontSize: '12px' } }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Button onClick={addRow} variant="contained" color="primary" style={{ marginTop: '10px', fontSize: '12px' }}>Add Row</Button>
            <div style={{ marginTop: '20px' }}>
                <TextField
                    label="Width of Deck"
                    type="number"
                    value={widthOfDeck}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    inputProps={{ style: { fontSize: '12px' } }}
                />
            </div>
            <div style={{ marginTop: '20px' }}>
                <h4>Number of Posts Needed</h4>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: '60px', padding: '4px' }}>Material</TableCell>
                                <TableCell style={{ width: '60px', padding: '4px' }}>Qty</TableCell>
                                {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((col) => (
                                    <TableCell key={col} style={{ width: '60px', padding: '4px' }}>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {postsData.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {Object.keys(row).map((key) => (
                                        <TableCell key={key} style={{ width: '60px', padding: '4px' }}>
                                            <TextField
                                                value={row[key]}
                                                variant="standard"
                                                InputProps={{ readOnly: true }}
                                                fullWidth
                                                size="small"
                                                inputProps={{ style: { fontSize: '12px' } }}
                                            />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div >
    );
};

export default DynamicSpreadsheet;

/*
Explanation:
1. State Management: Changed `initialData` to `postConfig` which is an object array, providing better data structure.
2. Input Handling: Updated the `handleInputChange` function to update specific properties based on column keys and added an HTTP PUT request to `/company` each time a value is changed.
3. Rendering Updates: Updated the rendering to manually map each property to a corresponding text field, avoiding the use of `Object.keys` for clarity.
*/
